import { db, analytics } from './config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';

// Helper to get start/end of day/month could be added here if needed for time-based filtering later

export async function getGlobalAnalytics() {
  try {
    const [jobsSnap, transactionsSnap] = await Promise.all([
      getDocs(collection(db, 'jobs')),
      getDocs(query(collection(db, 'transactions'), where('status', '==', 'completed')))
    ]);

    let totalCreditsUsed = 0;
    const jobTypeBreakdown = {};

    jobsSnap.forEach(doc => {
      const data = doc.data();
      const credits = data.creditsUsed || 0;
      const type = data.jobType || 'unknown';

      totalCreditsUsed += credits;
      jobTypeBreakdown[type] = (jobTypeBreakdown[type] || 0) + 1;

      // We could also sum credits per job type if needed:
      // if (!jobTypeBreakdown[type]) jobTypeBreakdown[type] = { count: 0, credits: 0 };
      // jobTypeBreakdown[type].count++;
      // jobTypeBreakdown[type].credits += credits;
    });

    let totalRevenue = 0;
    transactionsSnap.forEach(doc => {
      const data = doc.data();
      // Assuming amount is in standard units (e.g., dollars), not cents. 
      // If Stripe amounts are cents, checks `simplePayment.js` logic or assumed convention.
      // Based on `createSimpleStripePayment`, amount is passed directly. 
      // Usually Stripe uses cents, but let's assume the stored amount matches the display for now or check usage.
      // Verification showed `amount` passed to API. Let's assume it's the value to be displayed.
      totalRevenue += (Number(data.amount) || 0);
    });

    return {
      totalCreditsUsed, // "How much tokens spend"
      totalRevenue,     // "How much was spend"
      jobTypeBreakdown  // "Break down by what type of processing"
    };
  } catch (error) {
    console.error("Error fetching global analytics:", error);
    return { totalCreditsUsed: 0, totalRevenue: 0, jobTypeBreakdown: {} };
  }
}

export async function getTopUsersByJobs(limitCount = 10, page = 1, searchQuery = '') {
  // fetching all for aggregation (scalability warning: good for < 10k items)
  const [jobsSnap, transactionsSnap] = await Promise.all([
    getDocs(collection(db, 'jobs')),
    getDocs(query(collection(db, 'transactions'), where('status', '==', 'completed')))
  ]);

  const counts = new Map();
  // track credits per user too if needed, for "Break down by... search among users"
  const creditsPerUser = new Map();
  const revenuePerUser = new Map();
  const jobTypesPerUser = new Map();

  jobsSnap.forEach(d => {
    const data = d.data();
    const u = data.userId;
    if (!u) return;
    counts.set(u, (counts.get(u) || 0) + 1);
    creditsPerUser.set(u, (creditsPerUser.get(u) || 0) + (data.creditsUsed || 0));

    let types = jobTypesPerUser.get(u);
    if (!types) {
      types = {}; // Object to count occurrences: { 'excel_analysis': 5, ... }
      jobTypesPerUser.set(u, types);
    }
    const type = data.jobType || 'unknown';
    types[type] = (types[type] || 0) + 1;
  });

  transactionsSnap.forEach(d => {
    const data = d.data();
    const u = data.userId;
    if (!u) return;
    // Sum up revenue
    revenuePerUser.set(u, (revenuePerUser.get(u) || 0) + (Number(data.amount) || 0));
  });

  // enrich with user emails
  const usersSnap = await getDocs(collection(db, 'users'));
  const users = new Map();
  usersSnap.forEach(u => users.set(u.id, u.data()));

  // Build full list
  let results = Array.from(counts.entries()).map(([uid, cnt]) => {
    const userData = users.get(uid) || {};
    return {
      uid,
      count: cnt,
      creditsUsed: creditsPerUser.get(uid) || 0,
      totalRevenue: revenuePerUser.get(uid) || 0,
      jobTypeBreakdown: jobTypesPerUser.get(uid) || {},
      email: userData.email || '',
      name: userData.displayName || ''
    };
  });

  // Filter
  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    results = results.filter(r =>
      r.email.toLowerCase().includes(q) ||
      r.name.toLowerCase().includes(q) ||
      r.uid.toLowerCase().includes(q)
    );
  }

  // Sort by jobs count desc (default) or arguably revenue? Let's keep jobs count for now as per function name
  results.sort((a, b) => b.count - a.count);

  // Pagination
  const totalUsers = results.length;
  const totalPages = Math.ceil(totalUsers / limitCount);
  const startIndex = (page - 1) * limitCount;
  const paginatedResults = results.slice(startIndex, startIndex + limitCount);

  return {
    users: paginatedResults,
    totalUsers,
    totalPages,
    currentPage: page
  };
}

export async function getTopReferrers(limitCount = 10) {
  // assume each user has a field `referredBy` when they sign up with a code
  const usersSnap = await getDocs(collection(db, 'users'));
  const counts = new Map();
  usersSnap.forEach(u => {
    const data = u.data();
    const ref = data.referredBy || data.referred_by || null;
    if (!ref) return;
    counts.set(ref, (counts.get(ref) || 0) + 1);
  });
  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, limitCount);
  // Enrich with user info by referral code or email match
  const users = new Map();
  usersSnap.forEach(u => users.set(u.id, u.data()));
  return sorted.map(([refId, cnt]) => ({ refId, count: cnt, email: users.get(refId)?.email || refId, name: users.get(refId)?.displayName || '' }));
}

// Analytics Helper Functions

export const logAnalyticsEvent = (eventName, eventParams = {}) => {
  try {
    if (analytics) {
      logEvent(analytics, eventName, eventParams);
      console.log(`📊 Analytics Event: ${eventName}`, eventParams);
    }
  } catch (error) {
    console.warn('Analytics Error:', error);
  }
};

export const logPageView = (page_path) => {
  logAnalyticsEvent('page_view', { page_path });
};

export const logUserLogin = (method = 'email') => {
  logAnalyticsEvent('login', { method });
};

export const logUserSignup = (method = 'email') => {
  logAnalyticsEvent('sign_up', { method });
};

export const logFileAction = (action, fileType, fileSize) => {
  logAnalyticsEvent('file_action', { action, file_type: fileType, file_size: fileSize });
};

export const logJobAction = (action, jobType, jobId) => {
  logAnalyticsEvent('job_action', { action, job_type: jobType, job_id: jobId });
};

export const logPurchaseEvent = (currency, value, items) => {
  logAnalyticsEvent('purchase', { currency, value, items });
};
